import React, { ReactNode, useMemo, useState } from 'react';

import { FormComponent, UiComponent } from 'components';
import { usePageOption } from 'hooks';
import useModal from 'hooks/useModal';
import { UserGroupQuery } from 'queries';
import {
  IQuickMenu,
  IUserGroupListItem,
  SearchUserGroupFilterType,
} from 'types';
import * as PickerStyle from 'components/userGroup/Picker/style';

type GroupType = IQuickMenu['userGroups'][number];

interface Props {
  selectedId?: number;
  onSelect: (group: GroupType) => void;
}

const COLS: string[] = ['그룹명', '회원 수', '선택'];

function UserGroupPicker({ selectedId, onSelect }: Props) {
  const { handleClose } = useModal();
  const { pageOption, handlePage } = usePageOption(10);
  const [filter, setFilter] = useState<SearchUserGroupFilterType>({});

  const { data } = UserGroupQuery.useGetAll({
    page: pageOption.page,
    size: pageOption.size,
    ...filter,
  });

  const handleSearch = (d: SearchUserGroupFilterType) => {
    setFilter({ name: d.name?.trim() || undefined });
    handlePage({ ...pageOption, page: 1 });
  };

  const renderer: Record<
    (typeof COLS)[number],
    (data: IUserGroupListItem & { row: number }) => ReactNode
  > = useMemo(
    () => ({
      그룹명: ({ name }: IUserGroupListItem) => (
        <p css={{ textAlign: 'left' }}>{name}</p>
      ),
      '회원 수': ({ memberCount }: IUserGroupListItem) => (
        <>{memberCount.toLocaleString('ko')}명</>
      ),
      선택: ({ id, name }: IUserGroupListItem) => {
        const isSelected = id === selectedId;
        return (
          <UiComponent.Button
            variant={isSelected ? 'border' : 'contained'}
            color="primary"
            disabled={isSelected}
            onClick={() => {
              onSelect({ id, name });
              handleClose();
            }}
          >
            {isSelected ? '선택됨' : '선택'}
          </UiComponent.Button>
        );
      },
    }),
    [selectedId, onSelect, handleClose],
  );

  const rows = (data?.data || []).map((g: IUserGroupListItem) => ({
    ...g,
    rowKey: g.id,
  }));

  return (
    <div css={{ width: 480, maxWidth: '100%' }}>
      <FormComponent.Form css={PickerStyle.searchRow} submit={handleSearch}>
        <FormComponent.SearchInput
          name="name"
          placeholder="그룹명으로 검색해주세요"
          css={{ width: '100%' }}
        />
      </FormComponent.Form>
      <div css={PickerStyle.listWrap}>
        <UiComponent.Table
          cols={COLS}
          renderer={renderer}
          data={rows}
          sticky
          empty="등록된 회원 그룹이 없습니다"
        />
      </div>
      <UiComponent.Pagination
        pageOption={pageOption}
        handlePage={handlePage}
        totalPage={data?.totalPages || 1}
      />
    </div>
  );
}

export default UserGroupPicker;
